import { useEffect, useState } from 'react'
import { Loader2, MonitorPlay } from 'lucide-react'
import { apiService } from '@/service/api'
import { useTranslation } from '@/i18n'

interface VideoBriefPreviewProps {
  docId: string
  /** The row's status; anything but `success` means the brief is still rendering. */
  status?: string | null
}

/**
 * Inline player for a `video` document (a rendered "Explain as video" brief),
 * used by the board card and the catalog preview modal. The full page at
 * `/video/:id` adds the slide list; this is just the video.
 */
export default function VideoBriefPreview({ docId, status }: VideoBriefPreviewProps) {
  const { t } = useTranslation()
  const ready = status === undefined || status === null || status === 'success'
  const [mediaUrl, setMediaUrl] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    if (!ready) return
    let cancelled = false
    let objectUrl: string | null = null
    const controller = new AbortController()
    setFailed(false)
    setMediaUrl(null)
    void apiService
      .getVideoBriefMedia(docId, controller.signal)
      .then((blob) => {
        if (cancelled) return
        objectUrl = URL.createObjectURL(blob)
        setMediaUrl(objectUrl)
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })
    return () => {
      cancelled = true
      controller.abort()
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [docId, ready])

  if (!ready) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 h-full text-gray-500 dark:text-dark-text-muted">
        <MonitorPlay className="w-8 h-8" />
        <span className="text-sm">
          {t('data_catalog:file_still_processing', 'File is still being processed...')}
        </span>
      </div>
    )
  }
  if (failed) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500 dark:text-dark-text-muted">
        {t('document_viewer:video_brief.load_error', { defaultValue: 'The video could not be loaded.' })}
      </div>
    )
  }
  if (!mediaUrl) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400 dark:text-gray-500" />
      </div>
    )
  }
  return (
    <div className="h-full w-full bg-black flex items-center justify-center">
      <video src={mediaUrl} controls className="max-h-full max-w-full" data-testid="video-brief-preview" />
    </div>
  )
}
